import { useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

const Lightbox = ({ album, index, onClose, onNavigate }) => {
  const total = album.photos.length;
  const current = album.photos[index];

  const prev = useCallback(() => onNavigate((index - 1 + total) % total), [index, total, onNavigate]);
  const next = useCallback(() => onNavigate((index + 1) % total), [index, total, onNavigate]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", handler);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", handler);
      document.body.style.overflow = "";
    };
  }, [onClose, prev, next]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      data-testid="lightbox-modal-container"
      className="fixed inset-0 z-50 flex flex-col bg-[#0A0A0C]/95 backdrop-blur-xl"
      onClick={onClose}
    >
      <div className="flex items-center justify-between px-6 py-5 sm:px-10">
        <div>
          <h3 className="font-display text-lg font-semibold text-zinc-50">{album.title}</h3>
          <p className="font-tech text-xs tracking-[0.2em] text-zinc-500">
            {String(index + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
          </p>
        </div>
        <button
          data-testid="lightbox-close-btn"
          onClick={onClose}
          className="rounded-full border border-white/15 p-2.5 text-zinc-300 transition-colors hover:border-[#D4AF37]/60 hover:text-[#E2C275]"
          aria-label="Close gallery"
        >
          <X size={18} />
        </button>
      </div>

      <div className="relative flex flex-1 items-center justify-center px-4 pb-4 sm:px-20" onClick={(e) => e.stopPropagation()}>
        <button
          data-testid="lightbox-prev-btn"
          onClick={prev}
          className="absolute left-4 z-10 rounded-full border border-white/15 bg-[#0A0A0C]/60 p-3 text-zinc-200 backdrop-blur-sm transition-all hover:border-[#D4AF37]/60 hover:text-[#E2C275] sm:left-8"
          aria-label="Previous photo"
        >
          <ChevronLeft size={20} />
        </button>

        <AnimatePresence mode="wait">
          <motion.figure
            key={current.src}
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.3 }}
            className="flex max-h-full flex-col items-center"
          >
            <img
              data-testid="lightbox-current-image"
              src={current.src}
              alt={current.caption}
              className="max-h-[68vh] w-auto max-w-full rounded-xl border border-white/10 object-contain shadow-2xl"
            />
            <figcaption className="mt-5 flex flex-col items-center gap-2 sm:flex-row sm:gap-4">
              <span className="text-sm text-zinc-300">{current.caption}</span>
            </figcaption>
          </motion.figure>
        </AnimatePresence>

        <button
          data-testid="lightbox-next-btn"
          onClick={next}
          className="absolute right-4 z-10 rounded-full border border-white/15 bg-[#0A0A0C]/60 p-3 text-zinc-200 backdrop-blur-sm transition-all hover:border-[#D4AF37]/60 hover:text-[#E2C275] sm:right-8"
          aria-label="Next photo"
        >
          <ChevronRight size={20} />
        </button>
      </div>
    </motion.div>
  );
};

export default Lightbox;
